// src/components/deck_subdeck/SubdeckCard.tsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Lock, Unlock, Edit, Trash2, Download,
    BookOpen, Eye, FileText, Link, Link2Off, Check, ArrowRight, Gift, FolderInput, ExternalLink, Copy, MessageCircle
} from 'lucide-react';
import DragHandle from './DragHandle';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { resumoService } from '../../services/resumo.service';
import { DECK_COLORS, DECK_COLOR_OPTIONS } from './DeckGrid';
import { ICON_MAP } from './DeckIconPicker';
import './SubdeckCard.css';

interface Subdeck {
    _id: string;
    name: string;
    description?: string;
    cards: any[];
    cardCount?: number;
    createdAt: string;
    isBlocked?: boolean;
    hasResumo?: boolean;
    isFree?: boolean;
    type?: 'flashcards' | 'apostila';
    groupId?: string | null;
    colorId?: string | null;
    freeConfig?: {
        enabled: boolean;
        freeCardsLimit: number;
        freeApostilaPagesLimit: number;
    };
}

interface SubdeckCardProps {
    subdeck: Subdeck;
    isAdmin: boolean;
    isEditor?: boolean;
    canManage: boolean;
    masteryPercentage: number;
    deckId: string;
    deckName: string;
    deckIconId?: string | null;
    context: any;
    isLockedForUser: boolean;
    isPurchasedDeck: boolean;
    hasRead?: boolean;
    readPercentage?: number;
    pageCount?: number;
    isGridMode?: boolean;
    groupColor?: string;
    onStudy: () => void;
    onView: () => void;
    onToggleFree: () => void;
    onMove: () => void;
    onCopy?: () => void;
    onEdit: () => void;
    onExport: () => void;
    onDelete: () => void;
    onToggleBlock: () => void;
    onToggleIntegrarApostila: () => void;
    onMarkRead: () => void;
    onRename: (name: string, description: string, colorId?: string) => Promise<void>;
    onShowNoResumoModal: (name: string) => void;
    onPrefetch: () => void;
    onPrefetchPageData: () => void;
    onAssignGroup?: () => void;
}

const SubdeckCard: React.FC<SubdeckCardProps> = ({
    subdeck, isAdmin, isEditor = false, canManage, masteryPercentage,
    deckId, deckName, deckIconId, context, isLockedForUser, isPurchasedDeck, hasRead,
    readPercentage, pageCount, isGridMode,
    onStudy, onView, onEdit, onExport, onDelete, onMove, onCopy, onToggleFree,
    onToggleBlock, onToggleIntegrarApostila, onMarkRead, onRename,
    onShowNoResumoModal, onPrefetch, onPrefetchPageData, onAssignGroup,
}) => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuPosRef = useRef<{ top?: number; bottom?: number; right: number }>({ top: 0, right: 0 });
    const [descTooltipVisible, setDescTooltipVisible] = useState(false);
    const descBtnRef = useRef<HTMLButtonElement>(null);
    const descTooltipPos = useRef({ top: 0, right: 0 });
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameName, setRenameName] = useState(subdeck.name);
    const [renameDesc, setRenameDesc] = useState(subdeck.description || '');
    const [renameColorId, setRenameColorId] = useState(subdeck.colorId || 'blue-default');
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const kebabBtnRef = useRef<HTMLButtonElement>(null);

    const isApostila = subdeck.type === 'apostila';

    const resolvedColor = DECK_COLORS[subdeck.colorId || 'blue-default'] ?? '#185FA5';
    const DeckIconComp = deckIconId ? ICON_MAP[deckIconId] : null;

    const {
        attributes, listeners, setNodeRef,
        transform, transition, isDragging
    } = useSortable({ id: subdeck._id, disabled: !isAdmin });

    const cardStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : (subdeck.isBlocked ? 0.6 : (isLockedForUser ? 0.6 : 1)),
        '--sdc-color': resolvedColor,
        '--apc': resolvedColor,
    } as React.CSSProperties;

    const openMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (kebabBtnRef.current) {
            const rect = kebabBtnRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const right = window.innerWidth - rect.right;
            if (spaceBelow < 340) {
                menuPosRef.current = { bottom: window.innerHeight - rect.top + 4, right };
            } else {
                menuPosRef.current = { top: rect.bottom + 6, right };
            }
        }
        setMenuOpen(p => !p);
    };

    const handleDescEnter = () => {
        if (descBtnRef.current) {
            const rect = descBtnRef.current.getBoundingClientRect();
            descTooltipPos.current = {
                top: rect.top,
                right: window.innerWidth - rect.right,
            };
        }
        setDescTooltipVisible(true);
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
                kebabBtnRef.current && !kebabBtnRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    const navigateToResumo = () => {
        if (isAdmin) {
            navigate(`/resumo/${deckId}/${subdeck._id}`);
            return;
        }
        if (subdeck.isBlocked) return;
        if (!isPurchasedDeck && !subdeck.freeConfig?.enabled) { onStudy(); return; }
        if (!subdeck.hasResumo) { onShowNoResumoModal(subdeck.name); return; }
        navigate(`/resumo/${deckId}/${subdeck._id}`, {
            state: { isPurchasedDeck, freeApostilaPagesLimit: subdeck.freeConfig?.freeApostilaPagesLimit || 0 }
        });
    };

    const handleExportApostila = async () => {
        setMenuOpen(false);
        try {
            const data = await resumoService.getResumo(deckId, subdeck._id, false);
            resumoService.exportApostila(
                data.resumo,
                data.resumo.pages || [],
                deckName,
                subdeck.name
            );
        } catch {
            alert('Erro ao exportar apostila. Verifique se a apostila tem conteúdo.');
        }
    };

    const handleRenameSubmit = async () => {
        if (!renameName.trim()) return;
        setRenameSubmitting(true);
        try {
            await onRename(renameName.trim(), renameDesc.trim(), renameColorId);
            setShowRenameModal(false);
        } finally {
            setRenameSubmitting(false);
        }
    };

    const hasCards = !!(subdeck.cards?.length || subdeck.cardCount);

    return (
        <>
            <div
                ref={setNodeRef}
                style={cardStyle}
                id={`subdeck-${subdeck._id}`}
                className={`sdc-card ${isDragging ? 'sdc-dragging' : ''} ${subdeck.isBlocked ? 'sdc-blocked' : ''} ${isApostila ? 'sdc-card--apostila' : ''} ${isGridMode ? 'sdc-card--grid-mode' : ''}`}
            >
                {/* ── HEADER ── */}
                <div className="sdc-header">
                    <div className="sdc-icon-wrap" style={{ background: `${resolvedColor}20` }}>
                        {isApostila
                            ? <FileText size={16} style={{ color: resolvedColor }} />
                            : DeckIconComp
                                ? <DeckIconComp size={16} style={{ color: resolvedColor }} />
                                : <BookOpen size={16} style={{ color: resolvedColor }} />
                        }
                    </div>
                    <h3 className="sdc-name">
                        {subdeck.name}
                        {subdeck.isFree && (
                            <span className="sdc-badge-free"><Gift size={11} /> Gratuito</span>
                        )}
                    </h3>

                    <div className="sdc-header-actions">
                        {/* Cadeado (bloqueado ou locked para usuário) */}
                        {subdeck.isBlocked && (
                            <Lock size={15} className="sdc-lock-icon" />
                        )}
                        {isLockedForUser && !subdeck.isBlocked && (
                            <Lock size={14} className="sdc-lock-icon sdc-lock-icon--soft" />
                        )}

                        {/* Ícone de descrição (tooltip via portal) */}
                        {subdeck.description && (
                            <button
                                ref={descBtnRef}
                                className="sdc-action-btn sdc-btn-desc-icon"
                                onPointerDown={e => e.stopPropagation()}
                                onClick={e => e.stopPropagation()}
                                onMouseEnter={handleDescEnter}
                                onMouseLeave={() => setDescTooltipVisible(false)}
                                tabIndex={-1}
                            >
                                <MessageCircle size={14} />
                            </button>
                        )}

                        {/* Botão de ação principal — apostila: marcar como lido */}
                        {isApostila && (
                            <button
                                className={`sdc-action-btn sdc-btn-read-icon ${hasRead ? 'sdc-btn-read-icon--done' : ''}`}
                                title={hasRead ? 'Marcar como não lido' : 'Marcar como lido'}
                                onPointerDown={e => e.stopPropagation()}
                                onClick={e => { e.stopPropagation(); onMarkRead(); }}
                            >
                                <Check size={14} />
                            </button>
                        )}

                        {/* Admin: drag handle */}
                        {isAdmin && (
                            <div className="sdc-drag" {...attributes} {...listeners} onClick={e => e.stopPropagation()}>
                                <DragHandle isDragging={isDragging} />
                            </div>
                        )}

                        {/* Kebab menu: admin vê tudo; editor vê só "Editar cards" (não apostila) */}
                        {(isAdmin || (isEditor && !isApostila)) && (
                            <div className="sdc-kebab-wrap">
                                <button
                                    ref={kebabBtnRef}
                                    className="sdc-kebab-btn"
                                    onPointerDown={e => e.stopPropagation()}
                                    onClick={openMenu}
                                >⋮</button>

                                {menuOpen && createPortal(
                                    <div
                                        className="sdc-kebab-menu"
                                        ref={menuRef}
                                        style={{ top: menuPosRef.current.top, bottom: menuPosRef.current.bottom, right: menuPosRef.current.right }}
                                    >
                                        {/* Editar nome (modal inline) — só admin */}
                                        {isAdmin && (
                                            <button className="sdc-kebab-item" onClick={e => {
                                                e.stopPropagation();
                                                setMenuOpen(false);
                                                setRenameName(subdeck.name);
                                                setRenameDesc(subdeck.description || '');
                                                setShowRenameModal(true);
                                            }}>
                                                <Edit size={14} /> Editar nome
                                            </button>
                                        )}

                                        {/* Itens exclusivos do admin */}
                                        {isAdmin && (
                                            <>
                                                {/* Exportar apostila */}
                                                {isApostila && subdeck.hasResumo && (
                                                    <button className="sdc-kebab-item sdc-kebab-item--apostila" onClick={e => {
                                                        e.stopPropagation();
                                                        handleExportApostila();
                                                    }}>
                                                        <Download size={14} /> Exportar Apostila
                                                    </button>
                                                )}

                                                {/* Exportar subdeck (flashcards) */}
                                                {!isApostila && (
                                                    <button className="sdc-kebab-item" onClick={e => {
                                                        e.stopPropagation();
                                                        setMenuOpen(false);
                                                        onExport();
                                                    }}>
                                                        <Download size={14} /> Exportar
                                                    </button>
                                                )}

                                                <button className="sdc-kebab-item" onClick={e => {
                                                    e.stopPropagation();
                                                    setMenuOpen(false);
                                                    onMove();
                                                }}>
                                                    <ArrowRight size={14} /> Mover
                                                </button>

                                                {onCopy && (
                                                    <button className="sdc-kebab-item" onClick={e => {
                                                        e.stopPropagation();
                                                        setMenuOpen(false);
                                                        onCopy();
                                                    }}>
                                                        <Copy size={14} /> Copiar
                                                    </button>
                                                )}

                                                <button className={`sdc-kebab-item ${subdeck.isFree ? 'sdc-kebab-item--free-on' : ''}`} onClick={e => {
                                                    e.stopPropagation();
                                                    setMenuOpen(false);
                                                    onToggleFree();
                                                }}>
                                                    <Gift size={14} /> {subdeck.isFree ? 'Remover Gratuidade' : 'Liberar Gratuito'}
                                                </button>

                                                <button className="sdc-kebab-item" onClick={e => {
                                                    e.stopPropagation();
                                                    setMenuOpen(false);
                                                    onToggleBlock();
                                                }}>
                                                    {subdeck.isBlocked
                                                        ? <><Unlock size={14} /> Disponibilizar</>
                                                        : <><Lock size={14} /> Interditar</>}
                                                </button>

                                                {/* Integrar apostila — só flashcards */}
                                                {!isApostila && (
                                                    <button
                                                        className={`sdc-kebab-item ${subdeck.hasResumo ? 'sdc-kebab-item--active' : ''}`}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setMenuOpen(false);
                                                            onToggleIntegrarApostila();
                                                        }}
                                                    >
                                                        {subdeck.hasResumo
                                                            ? <><Link2Off size={14} /> Remover Apostila</>
                                                            : <><Link size={14} /> Integrar Apostila</>}
                                                    </button>
                                                )}

                                                {onAssignGroup && (
                                                    <button className="sdc-kebab-item" onClick={e => {
                                                        e.stopPropagation();
                                                        setMenuOpen(false);
                                                        onAssignGroup();
                                                    }}>
                                                        <FolderInput size={14} /> Mover para grupo
                                                    </button>
                                                )}

                                                <div className="sdc-kebab-divider" />

                                                <button className="sdc-kebab-item sdc-kebab-item--danger" onClick={e => {
                                                    e.stopPropagation();
                                                    setMenuOpen(false);
                                                    onDelete();
                                                }}>
                                                    <Trash2 size={14} /> Excluir
                                                </button>
                                            </>
                                        )}
                                    </div>,
                                    document.body
                                )}
                            </div>
                        )}

                        {/* canManage não-admin: só botão de deletar */}
                        {!isAdmin && canManage && (
                            <button className="sdc-btn-icon sdc-btn-icon--delete" onClick={e => { e.stopPropagation(); onDelete(); }}>
                                <Trash2 size={15} />
                            </button>
                        )}
                    </div>
                </div>

                {/* ── APOSTILA CARD ── */}
                {isApostila ? (
                    <>
                        {/* Stats de leitura */}
                        {(pageCount !== undefined && pageCount > 0) && (
                            <div className="sdc-reading-stats">
                                <div className="sdc-stat">
                                    <span className="sdc-reading-stat-value">{pageCount}</span>
                                    <span className="sdc-reading-stat-label">págs</span>
                                </div>
                                <div className="sdc-reading-divider" />
                                <div className="sdc-reading-progress">
                                    <span className="sdc-reading-stat-label">Leitura</span>
                                    <div className="sdc-mastery-row">
                                        <div className="sdc-reading-bar">
                                            <div
                                                className="sdc-reading-fill"
                                                style={{ width: `${hasRead ? 100 : (readPercentage ?? 0)}%` }}
                                            />
                                        </div>
                                        <span className="sdc-reading-pct">
                                            {hasRead === undefined ? '—' : `${hasRead ? 100 : (readPercentage ?? 0)}%`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="sdc-apostila-body">
                            <button
                                className="sdc-btn-apostila-main"
                                onPointerEnter={onPrefetchPageData}
                                onClick={e => { e.stopPropagation(); navigateToResumo(); }}
                            >
                                <FileText size={16} />
                                Acessar Apostila
                            </button>
                        </div>
                    </>
                ) : (
                    /* ── FLASHCARD CARD ── */
                    <>
                        <div className="sdc-info-row">
                            <span className="sdc-card-count">
                                {subdeck.cardCount || subdeck.cards?.length || 0} cards
                            </span>
                        </div>

                        <div className="sdc-mastery-wrap">
                            <div className="sdc-mastery-label-row">
                                <span className="sdc-mastery-lbl">Domínio</span>
                                <span className="sdc-mastery-val">
                                    {masteryPercentage >= 0 ? `${masteryPercentage}%` : '—'}
                                </span>
                            </div>
                            <div className="sdc-mastery-segs">
                                {[0, 1, 2, 3, 4].map(i => {
                                    const fill = Math.min(100, Math.max(0, ((masteryPercentage >= 0 ? masteryPercentage : 0) - i * 20) / 20 * 100));
                                    return (
                                        <div key={i} className="sdc-mastery-seg">
                                            <div className="sdc-mastery-seg-fill" style={{ width: `${fill}%`, background: resolvedColor }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="sdc-cta">
                            <button
                                className="sdc-btn-study"
                                style={{ background: resolvedColor }}
                                onPointerEnter={onPrefetch}
                                onClick={e => { e.stopPropagation(); onStudy(); }}
                                disabled={!hasCards}
                            >
                                <BookOpen size={14} /> Estudar
                            </button>
                            <button
                                className="sdc-cta-outline-btn"
                                title="Visualizar cards"
                                onPointerEnter={onPrefetchPageData}
                                onPointerDown={e => e.stopPropagation()}
                                onClick={e => { e.stopPropagation(); onView(); }}
                                disabled={!hasCards}
                            >
                                <Eye size={14} />
                            </button>
                            {(isAdmin || isEditor) && (
                                <button
                                    className="sdc-cta-outline-btn"
                                    title="Editar cards"
                                    onPointerDown={e => e.stopPropagation()}
                                    onClick={e => { e.stopPropagation(); onEdit(); }}
                                >
                                    <ExternalLink size={14} />
                                </button>
                            )}
                            {subdeck.hasResumo && (
                                <button
                                    className="sdc-cta-outline-btn"
                                    title="Acessar apostila"
                                    onPointerEnter={onPrefetchPageData}
                                    onPointerDown={e => e.stopPropagation()}
                                    onClick={e => { e.stopPropagation(); navigateToResumo(); }}
                                >
                                    <FileText size={14} />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ── TOOLTIP DESCRIÇÃO (portal, sempre por cima de tudo) ── */}
            {descTooltipVisible && subdeck.description && createPortal(
                <div
                    className="sdc-desc-tooltip-portal"
                    style={{ top: descTooltipPos.current.top, right: descTooltipPos.current.right }}
                >
                    {subdeck.description}
                </div>,
                document.body
            )}

            {/* ── MODAL RENOMEAR (portal para escapar de stacking contexts de grupos) ── */}
            {showRenameModal && createPortal(
                <div className="sdc-rename-overlay" onClick={() => setShowRenameModal(false)}>
                    <div className="sdc-rename-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Editar {isApostila ? 'Apostila' : 'Subdeck'}</h3>
                        <input
                            className="sdc-rename-input"
                            value={renameName}
                            onChange={e => setRenameName(e.target.value)}
                            placeholder="Nome"
                            autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(); }}
                        />
                        <textarea
                            className="sdc-rename-textarea"
                            value={renameDesc}
                            onChange={e => setRenameDesc(e.target.value)}
                            placeholder="Descrição (opcional)"
                            rows={3}
                        />
                        <div style={{ marginTop: 12, marginBottom: 4 }}>
                            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cor da barra</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {DECK_COLOR_OPTIONS.map(opt => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        title={opt.label}
                                        onClick={() => setRenameColorId(opt.id)}
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            background: opt.hex,
                                            border: renameColorId === opt.id ? '2px solid #fff' : '2px solid transparent',
                                            outline: renameColorId === opt.id ? `2px solid ${opt.hex}` : 'none',
                                            cursor: 'pointer',
                                            padding: 0,
                                            transition: 'outline 0.15s',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="sdc-rename-actions">
                            <button className="sdc-rename-cancel" onClick={() => setShowRenameModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="sdc-rename-save"
                                onClick={handleRenameSubmit}
                                disabled={renameSubmitting || !renameName.trim()}
                            >
                                {renameSubmitting ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default SubdeckCard;
